import * as DataController from './controllers/data_controller';
import { BUTTON_STYLES, SHADOWS, SHAPE_STYLES, TEXT_STYLE } from './constants';
import { COLORS } from './constants-colors';
import {
  addGameObject,
  anchoredPos,
  getEntityStateAndProgress,
  getZeroEntity,
  getZeroStaticEntity,
  setParent,
} from './entity';
import { addV2, cloneTextStyle, Ease, isEqual, lerpNumber, lerpV2, scaleV2, v2 } from './helpers';
import { setVisibilityState } from './simulate';
import {
  ButtonStyle,
  Cursor,
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  IconButton,
  MoveMode,
  RenderItemType,
  Shape,
  SharedObjectType,
  TextAppearStyle,
  type V2,
} from './types';
import { Alignment_Horizontal, Side } from './shared/api_shared_types';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import logger from './controllers/logger';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageImage,
  type RenderPackageItem,
} from './render_package';
import { dt } from '.';

export function controller(): GameObjectController {
  return {
    getNew: getZeroIconButton,
    sync: null,
    update,
    createPackage,
    dispose,
  };
}

export function getZeroIconButton(ent: Entity): IconButton {
  let btn: IconButton = {
    ...ent,
    targetIconScale: 0,
    iconScale: 1,
    offsetPos: v2(),
    targetOffsetPos: v2(),
    showOptions: false,
    highlightOption: -1,
    colorBack: COLORS.WHITE_100,
    color: COLORS.WHITE_100,
    hoverLabel: '',
    style: BUTTON_STYLES.CONTINUE,
    requestNewStyle: null,
    checked: false,
    objectType: SharedObjectType.ICON_BUTTON,
    tooltip: '',
    label: '',
    labelSide: Side.NONE,
    tObjLabel: null,
    iconRotation: 0,
    iconRotationSpeed: 0,
    hoverMoveStrength: 0.3,
    hoverGrowStrength: 1.2,
  };

  btn.shape = Shape.CIRCLE;
  btn.radius = 26;
  btn.allowDrag = false;
  btn.influenceKinetic = true;

  return btn;
}

export function createAndAddLocalIconButton(
  manager: GameManager,
  style: ButtonStyle = BUTTON_STYLES.CONTINUE,
): IconButton {
  let button = getZeroIconButton(getZeroEntity());
  button.style = { ...style };
  button.clickable = true;
  button.radius = button.style.size;

  addGameObject(manager, button);

  setVisibilityState(button, VisibilityState.APPEARING);

  return button;
}

function update(button: IconButton) {
  let isDirty = false;

  const state = button.visibilityState;
  const progress = button.stateProgress;
  if (state === VisibilityState.APPEARING) {
    button.iconScale = Ease.outBack(progress);
    button.targetIconScale = button.scale;
    isDirty = true;
  } else if (state === VisibilityState.DISAPPEARING || state === VisibilityState.HIDING) {
    button.iconScale = 1 - Ease.inBack(progress);
    button.targetIconScale = button.scale;
    isDirty = true;
  } else if (state === VisibilityState.HIDDEN && button.iconScale !== 0) {
    button.iconScale = 0;
    button.targetIconScale = 0;
    isDirty = true;
  }

  if (button.iconRotationSpeed) {
    button.iconRotation += dt * button.iconRotationSpeed;
    isDirty = true;
  }

  if (button.requestNewStyle) {
    button.style = button.requestNewStyle;
    isDirty = true;
    button.requestNewStyle = null;
  }

  if (button.tooltip || button.label) {
    if (button.startHighlightThisFrame) {
      if (button.tooltip) {
        // button.manager.cursor.requestChangeTooltip = button.tooltip;
      }

      if (button.label) {
        showLabel(button);
      }
    } else if (button.stopHighlightThisFrame) {
      if (button.tooltip) {
        // button.manager.cursor.requestHideTooltip = button.tooltip;
      }

      if (button.label) {
        hideLabel(button);
      }
    }
  }

  if (button.visibilityState === VisibilityState.DEFAULT) {
    if (button.highlighted) {
      if (!button.mouseDownOnMe) {
        button.targetIconScale = button.hoverGrowStrength;
      } else {
        button.targetIconScale = 0.9;
        hideLabel(button);
      }

      // button.targetOffsetPos = scaleV2(button.relativeMousePos, button.hoverMoveStrength);

      if (button.clickedThisFrame) {
        if (button.style === BUTTON_STYLES.CHECK && button.serverObjId === -1) {
          //Server controlled buttons are checked by the server, not locally
          button.checked = !button.checked;
        }
      }
    } else {
      button.targetIconScale = 1;
      button.targetOffsetPos = v2();
    }
    button.highlightOption = -1;

    const scale = lerpNumber(button.iconScale, button.targetIconScale, 0.3);
    if (button.iconScale !== scale) {
      button.iconScale = scale;
      isDirty = true;
    }
    const offsetPos = lerpV2(button.offsetPos, button.targetOffsetPos, 0.3);
    if (!isEqual(button.offsetPos, offsetPos)) {
      button.offsetPos = offsetPos;
      isDirty = true;
    }
  }

  return isDirty;
}

function dispose(object: IconButton) {
  // if (object.tooltip && object.manager.cursor.tooltip === object.tooltip) {
  //   object.manager.cursor.requestHideTooltip = object.tooltip;
  // }
}

function showLabel(btn: IconButton) {
  if (btn.label) {
    if (!btn.tObjLabel) {
      let style = cloneTextStyle(TEXT_STYLE.ICON_BUTTON_LABEL);
      let appearStyle = TextAppearStyle.FADE;

      if (btn.labelSide === Side.LEFT) {
        style.textAlign = Alignment_Horizontal.RIGHT;
      } else if (btn.labelSide === Side.RIGHT) {
        style.textAlign = Alignment_Horizontal.LEFT;
      } else if (btn.labelSide === Side.ABOVE) {
        style.textAlign = Alignment_Horizontal.CENTER;
      } else if (btn.labelSide === Side.BELOW) {
        style.textAlign = Alignment_Horizontal.CENTER;
      }
      btn.tObjLabel = createAndAddLocalTextObject(
        btn.manager,
        getZeroStaticEntity(),
        btn.label,
        style,
        2,
        false,
        0,
        1,
        appearStyle,
      );
      btn.tObjLabel.appearDuration = 0.1;
      btn.tObjLabel.onlyShowInDefaultState = false;
      btn.tObjLabel.moveMode = MoveMode.ANCHORED;
      const offset = 2 + 10 * (btn.hoverGrowStrength - 1);

      if (btn.labelSide === Side.LEFT) {
        btn.tObjLabel.anchoredPos = anchoredPos(v2(0, 0.5), v2(-offset, 0.5), v2(1, 0.5));
      } else if (btn.labelSide === Side.RIGHT) {
        btn.tObjLabel.anchoredPos = anchoredPos(v2(1, 0.5), v2(offset, 0.5), v2(0, 0.5));
      } else if (btn.labelSide === Side.ABOVE) {
        btn.tObjLabel.anchoredPos = anchoredPos(v2(0.5, 0), v2(0, -offset), v2(0.5, 1));
      } else if (btn.labelSide === Side.BELOW) {
        btn.tObjLabel.anchoredPos = anchoredPos(v2(0.5, 1), v2(0, offset), v2(0.5, 0));
      }
      setParent(btn.tObjLabel, btn);
    }
  }
}

function hideLabel(btn: IconButton) {
  if (btn.tObjLabel) {
    setVisibilityState(btn.tObjLabel, VisibilityState.DISAPPEARING);
    btn.tObjLabel = null;
  }
}

export function setLabel(btn: IconButton, label: string) {
  if (!btn || !label) return;

  if (label !== btn.label) {
    btn.label = label;
    if (label === '') {
      if (btn.tObjLabel) {
        setVisibilityState(btn.tObjLabel, VisibilityState.DISAPPEARING);
      }
    } else {
      if (btn.tObjLabel) {
        changeTextObjectText(btn.tObjLabel, btn.label);
      }
    }
  }
}

export function createPackage(btn: IconButton, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const pos = addV2(position, scaleV2(btn.offsetPos, scale));

  const viewHighlight = btn.highlighted && !btn.mouseDownOnMe;
  const color = viewHighlight ? btn.style.colorHighlight : btn.style.color;
  const showShadow = btn.style.hoverShadow && viewHighlight;
  const shadow = showShadow ? SHADOWS.SHADOW_BUTTON_HIGHLIGHT : null;

  const circle: RenderPackageCircle = {
    type: RenderItemType.circle,
    ...EMPTY_RENDER_TRANSFORM,
    scale: scale * btn.iconScale,
    positionX: pos.x,
    positionY: pos.y,
    radius: btn.radius,
    shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: color },
    shadow: shadow,
  };
  rPackage.push(circle);

  const imageState = DataController.getImageState(btn.style.icon);
  const size = btn.style.iconSize;
  const offset = -size / 2;

  const iconPos = addV2(pos, v2(offset, offset));

  if (imageState === 'success' && size > 0) {
    const im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale: scale * btn.iconScale,
      imageId: btn.style.icon,
      positionX: pos.x,
      positionY: pos.y,
      positionLocalX: offset,
      positionLocalY: offset,
      shape: Shape.RECT,
      radius: 0,
      opacity: 1,

      rotation: btn.iconRotation,
      width: size,
      height: size,
      flipHorizontally: btn.style.flipHorizontally,
    };
    rPackage.push(im);
  }

  return rPackage;
}
